import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';
import { Subject, takeUntil } from 'rxjs';

// PrimeNG imports
import { Drawer } from 'primeng/drawer';
import { DividerModule } from 'primeng/divider';
import { ButtonModule } from 'primeng/button';
import { PanelMenuModule } from 'primeng/panelmenu';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-sidenav',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    Drawer,
    DividerModule,
    ButtonModule,
    PanelMenuModule,
  ],
  templateUrl: './sidenav.component.html',
  styleUrl: './sidenav.component.css',
})
export class SidenavComponent implements OnInit, OnDestroy {
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  menuItems: MenuItem[] = [];
  isMobile = false;
  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private translate: TranslateService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.initializeMenu();
    this.checkScreenSize();

    // Update menu when language changes
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.initializeMenu();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.checkScreenSize();
  }

  private checkScreenSize(): void {
    this.isMobile = window.innerWidth <= 768;
  }

  onHide(): void {
    // Only emit hide event on mobile (when modal is dismissible)
    if (this.isMobile) {
      this.visibleChange.emit(false);
    }
  }

  private initializeMenu(): void {
    this.menuItems = [
      {
        label: this.translate.instant('platformInsights.title'),
        icon: 'pi pi-chart-line',
        routerLink: ['/platform-insights'],
        command: () => this.onMenuItemClick(),
      },
      {
        label: this.translate.instant('navigation.launchpadProjects'),
        icon: 'pi pi-briefcase',
        items: [
          {
            label: this.translate.instant('projectOwners.title'),
            icon: 'pi pi-users',
            routerLink: ['/project-owners'],
            command: () => this.onMenuItemClick(),
          },
          {
            label: this.translate.instant('projects.title'),
            icon: 'pi pi-briefcase',
            routerLink: ['/projects'],
            command: () => this.onMenuItemClick(),
          },
          {
            label: this.translate.instant('categories.title'),
            icon: 'pi pi-tag',
            routerLink: ['/categories'],
            command: () => this.onMenuItemClick(),
          },
          {
            label: this.translate.instant('userMlp.title'),
            icon: 'pi pi-wallet',
            routerLink: ['/user-mlp'],
            command: () => this.onMenuItemClick(),
          },
        ],
      },
      {
        label: this.translate.instant('navigation.contentManagement'),
        icon: 'pi pi-file',
        items: [
          {
            label: this.translate.instant('successStories.title'),
            icon: 'pi pi-bookmark',
            routerLink: ['/success-stories'],
            command: () => this.onMenuItemClick(),
          },
          {
            label: this.translate.instant('learnMore.title'),
            icon: 'pi pi-book',
            routerLink: ['/learn-more'],
            command: () => this.onMenuItemClick(),
          },
          {
            label: this.translate.instant('community.title'),
            icon: 'pi pi-share-alt',
            routerLink: ['/community'],
            command: () => this.onMenuItemClick(),
          },
        ],
      },
      {
        label: this.translate.instant('navigation.adminSettings'),
        icon: 'pi pi-cog',
        items: [
          {
            label: this.translate.instant('adminUsers.title'),
            icon: 'pi pi-user-edit',
            routerLink: ['/admin-users'],
            command: () => this.onMenuItemClick(),
          },
          {
            label: this.translate.instant('blockchains.title'),
            icon: 'pi pi-link',
            routerLink: ['/blockchains'],
            command: () => this.onMenuItemClick(),
          },
        ],
      },
    ];
  }

  onMenuItemClick(): void {
    // Close drawer on mobile when menu item is clicked
    if (this.isMobile) {
      this.visibleChange.emit(false);
    }
  }

  logout(): void {
    this.authService.logout(true).subscribe();
  }
}
